import { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useMatches, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { PageBreadcrumb } from "./PageBreadcrumb";
import { useSession } from "@/contexts/SessionContext";
import { usePlatformSettingsContext } from "@/contexts/PlatformSettingsContext";
import { hasConsentChoice } from "@/lib/cookie-consent";
import {
  APP_HOST,
  APP_HOST_MARKETING_REDIRECTS,
  marketingUrl,
} from "@/lib/marketingUrl";

// Phase SEO-2 — On app.youtemp.co.uk, marketing routes are duplicates of the
// canonical marketing host. Set noindex/nofollow synchronously and redirect
// to the canonical trailing-slash URL on youtemp.co.uk. Runs at module load
// so it fires before any React render or child effect.
if (typeof window !== "undefined" && window.location.hostname === APP_HOST) {
  const rawPath = window.location.pathname || "/";
  const normalised = rawPath === "/" ? "/" : rawPath.replace(/\/+$/, "");
  if (APP_HOST_MARKETING_REDIRECTS.has(normalised)) {
    try {
      let robotsMeta = document.querySelector(
        'meta[name="robots"]'
      ) as HTMLMetaElement | null;
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.name = "robots";
        (document.head || document.documentElement).appendChild(robotsMeta);
      }
      robotsMeta.content = "noindex,nofollow";
    } catch {
      // ignore — the redirect below is the primary mitigation
    }
    window.location.replace(marketingUrl(normalised));
  }
}

import {
  FooterLazy as LazyFooter,
  CookieConsentBannerLazy as LazyCookieConsentBanner,
  MaintenanceBannerLazy as LazyMaintenanceBanner,
} from "@/lib/prerenderedHomeChunks";
import { dismissSplash } from "@/lib/splash";

import { usePageTracking } from "@/hooks/usePageTracking";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useWindowScrollDirection } from "@/hooks/useWindowScrollDirection";
import { initializeGA, disableGA, enableGA, trackPageView, sanitizePath } from "@/lib/analytics";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface RouteHandle {
  breadcrumbs?: BreadcrumbItem[];
  breadcrumbsJsonLd?: boolean;
}

/**
 * Routes that render as a full-bleed app shell: header + breadcrumb stay,
 * footer is suppressed, and the page itself does not scroll (children
 * manage their own internal scrolling). Path is normalised (trailing
 * slash stripped) before matching.
 */
const APP_SHELL_PATHS = new Set<string>(["/workforce-hive"]);

/**
 * Tiny component inside Suspense that dismisses splash once lazy content mounts.
 */
function SplashDismisser() {
  const dismissed = useRef(false);
  useEffect(() => {
    if (!dismissed.current) {
      dismissed.current = true;
      dismissSplash();
    }
  }, []);
  return null;
}

/**
 * PublicShell is a persistent layout wrapper for all public marketing pages.
 * The Header and Footer stay mounted across route changes, preventing flash/remount.
 * Only the content area (Outlet) suspends during lazy-load transitions.
 */
export function PublicShell() {
  const location = useLocation();
  const normalisedPath =
    location.pathname !== "/" && location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;
  const isAppShell = APP_SHELL_PATHS.has(normalisedPath);
  // Phase 4 — gate MaintenanceBanner so its chunk is never requested for
  // anonymous public visitors (the only audience PSI measures). Banner
  // also internally returns null when there is no user, so this only
  // mirrors that decision at the import boundary.
  const { user } = useSession();
  const { maintenanceConfig } = usePlatformSettingsContext();
  const showMaintenance = Boolean(
    user &&
      (maintenanceConfig.maintenance_mode ||
        maintenanceConfig.system_announcement),
  );

  // Phase 4 — gate CookieConsentBanner on stored consent. Cached visitors
  // (the prerendered-homepage audience after first acceptance) skip the
  // import entirely. Fresh visitors get the banner mounted on first user
  // interaction (pointerdown/keydown/touchstart/scroll) or after an 8s
  // fallback, so the chunk never lands inside the PSI LCP/TBT window.
  const [consentKnown] = useState(() => hasConsentChoice());
  const [mountCookieBanner, setMountCookieBanner] = useState(false);
  useEffect(() => {
    if (consentKnown) return;
    // Bail out entirely when the user requested data-saver mode — they
    // can still open the preferences from the floating tab after first
    // interaction in a future phase; today we simply do not push the
    // chunk on them.
    const conn = (navigator as Navigator & {
      connection?: { saveData?: boolean };
    }).connection;
    if (conn?.saveData) return;

    const events: Array<{ name: string; passive: boolean }> = [
      { name: "pointerdown", passive: true },
      { name: "keydown", passive: false },
      { name: "touchstart", passive: true },
      { name: "scroll", passive: true },
    ];
    let fired = false;
    let timeoutId: number | null = null;
    const fire = () => {
      if (fired) return;
      fired = true;
      for (const { name, passive } of events) {
        window.removeEventListener(name, fire, { capture: true } as EventListenerOptions);
        // Some browsers track listeners by (type, listener, useCapture)
        // only — the explicit removeEventListener above covers them.
        void passive;
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      setMountCookieBanner(true);
    };
    for (const { name, passive } of events) {
      window.addEventListener(name, fire, {
        capture: true,
        once: true,
        passive,
      });
    }
    timeoutId = window.setTimeout(fire, 8000);
    return () => {
      for (const { name } of events) {
        window.removeEventListener(name, fire, { capture: true } as EventListenerOptions);
      }
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [consentKnown]);

  // Phase 1 — `consentReady` replaces the previous PlatformSettingsContext
  // subscription. PublicShell no longer reads platform settings directly;
  // the consent context internally still waits on the public-flags load to
  // know whether bypass_cookie_consent_banner is set.
  const { analyticsEnabled, consentReady } = useCookieConsent();
  const initialPageTracked = useRef(false);
  // Capture the true landing URL once at mount. If the user navigates before
  // GA finishes loading, window.location would no longer reflect the page
  // they actually landed on — this ref preserves it.
  const landingPathRef = useRef<string | null>(null);
  if (landingPathRef.current === null && typeof window !== "undefined") {
    landingPathRef.current = sanitizePath(
      window.location.pathname + window.location.search
    );
  }

  // Initialize or disable GA based on cookie consent
  // CRITICAL: Wait for flags to load before making any GA calls to prevent race condition
  // where disableGA() sets a permanent opt-out flag before bypass consent is known
  useEffect(() => {
    // Don't do anything while the consent gate hasn't settled — prevents
    // a premature disableGA() call before the bypass flag is known.
    if (!consentReady) return;

    if (!analyticsEnabled) {
      disableGA();
      initialPageTracked.current = false;
      return;
    }

    // Defer GA bootstrap past first paint so `gtag/js` doesn't compete
    // with the homepage hero for main-thread time. Idempotent — guards
    // inside `initializeGA` prevent duplicate loads if the consent flag
    // flips mid-idle.
    const runInit = () => {
      // Clear any previous opt-out flag before initializing (defensive measure)
      enableGA();
      initializeGA().then(() => {
        if (!initialPageTracked.current) {
          initialPageTracked.current = true;
          // Use the captured landing URL, not the current location — the
          // user may have already navigated while GA was loading.
          const landing = landingPathRef.current
            ?? sanitizePath(window.location.pathname + window.location.search);
          trackPageView(landing);
        }
      });
    };

    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    }).requestIdleCallback;
    let idleHandle: number | undefined;
    let timeoutHandle: number | undefined;
    if (typeof ric === 'function') {
      idleHandle = ric(runInit, { timeout: 2000 });
    } else {
      timeoutHandle = window.setTimeout(runInit, 1500);
    }
    return () => {
      const cic = (window as Window & {
        cancelIdleCallback?: (handle: number) => void;
      }).cancelIdleCallback;
      if (idleHandle !== undefined) cic?.(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, [analyticsEnabled, consentReady]);
  
  // Track page views for GA4 - only if analytics enabled
  // Note: usePageTracking checks if gtag is available before tracking
  usePageTracking();
  
  // Get breadcrumbs from route handle metadata
  const matches = useMatches();
  const currentMatch = matches[matches.length - 1];
  const handle = currentMatch?.handle as RouteHandle | undefined;
  const breadcrumbs = handle?.breadcrumbs;
  const breadcrumbsJsonLd = handle?.breadcrumbsJsonLd !== false;

  // Auto-hide header + breadcrumb stack on scroll down, restore on scroll up.
  // Pass the current pathname as a reset key so the header is forced visible
  // on every route change (otherwise a hidden header survives the navigation
  // and the new page renders with the chrome translated off-screen).
  const chromeHidden = useWindowScrollDirection(24, location.key ?? location.pathname);

  // Scroll to top on route change (replaces global ScrollToTop component)
  // Use manual scroll restoration for bfcache compatibility
  // Skip initial mount — browser is already at (0,0), calling scrollTo forces a reflow
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // scrollTo is a write-only call — no layout read, no forced reflow.
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Safety timeout: dismiss splash after 3s even if something breaks
  useEffect(() => {
    const timeout = setTimeout(() => dismissSplash(), 1500);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      {showMaintenance && (
        <Suspense fallback={null}><LazyMaintenanceBanner /></Suspense>
      )}
      <div
        className={
          isAppShell
            ? "flex flex-col h-[100dvh] overflow-hidden overscroll-contain"
            : "min-h-screen flex flex-col"
        }
      >
        {/* Skip to main content link for accessibility */}
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none"
        >
          Skip to main content
        </a>
        <Header hidden={chromeHidden} />
        {breadcrumbs && breadcrumbs.length > 0 && !isAppShell && (
          <PageBreadcrumb items={breadcrumbs} emitJsonLd={breadcrumbsJsonLd} hidden={chromeHidden} />
        )}
        {breadcrumbs && breadcrumbs.length > 0 && isAppShell && (
          // App-shell routes (e.g. /workforce-hive) hide the breadcrumb to
          // reclaim vertical space on mobile while keeping JSON-LD for SEO.
          <div className="hidden md:block">
            <PageBreadcrumb items={breadcrumbs} emitJsonLd={breadcrumbsJsonLd} hidden={chromeHidden} />
          </div>
        )}
        <SplashDismisser />
        <main
          id="main-content"
          className={
            isAppShell
              ? "flex-1 min-h-0 overflow-hidden overscroll-contain"
              : "flex-1 overflow-x-clip min-h-[calc(100vh-5rem)] min-h-[calc(100dvh-5rem)]"
          }
        >
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </main>
        {!isAppShell && (
          <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
            <LazyFooter />
          </Suspense>
        )}
        {!consentKnown && mountCookieBanner && (
          <Suspense fallback={null}><LazyCookieConsentBanner /></Suspense>
        )}
      </div>
    </>
  );
}
